import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PublicHeader, Breadcrumbs, SeoHead } from "@/components/PublicSeo";
import { SeoFooter } from "@/components/SeoFooter";
import NotFound from "@/pages/NotFound";
import { BadgeCheck, MapPin, ShieldCheck } from "lucide-react";

const compact = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n));

export default function CreatorPublic() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/creators/${slug}`).then((r) => setD(r.data)).catch(() => setD({ status: "not_found" }));
  }, [slug]);
  if (!d) return <div className="min-h-screen bg-background"><PublicHeader /><p className="p-10 text-muted-foreground">Loading…</p></div>;
  if (d.status === "not_found") return <NotFound />;
  if (d.status === "redirect") return <Navigate to={`/creators/${d.slug}`} replace />;
  const c = d.creator;
  const where = [c.city, c.country].filter(Boolean).join(", ");
  const title = `${c.name}${c.categories[0] ? ` – ${c.categories[0]} Creator` : ""}${c.city ? ` in ${c.city}` : ""} | InfluencerCollab`;
  const desc = `${c.name} is a ${c.categories.slice(0, 2).join(" & ") || "content"} creator${where ? ` based in ${where}` : ""}${c.platforms.length ? ` on ${c.platforms.slice(0, 3).join(", ")}` : ""}. View stats and invite them to a brand collaboration on InfluencerCollab.`.slice(0, 165);
  return (
    <div className="min-h-screen bg-background" data-testid="creator-public-page">
      <SeoHead title={title} description={desc} canonical={d.canonical_url} robots="index,follow" ogType="profile" />
      <PublicHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <Breadcrumbs items={[{ label: "Home", path: "/" }, { label: "Influencers", path: "/influencer-directory" }, { label: c.name, path: `/creators/${c.slug}` }]} />
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <img src={c.picture} alt={`${c.name} profile photo`} width="120" height="120" className="h-28 w-28 rounded-full object-cover border border-border" referrerPolicy="no-referrer" />
          <div className="min-w-0">
            <h1 className="font-heading font-black text-4xl tracking-tight" data-testid="creator-public-name">{c.name}</h1>
            {where && <p className="text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-4 w-4" />{where}</p>}
            {c.creator_type && <p className="text-sm mt-1">{c.creator_type}</p>}
            <p className="mt-3 text-muted-foreground max-w-2xl">{c.bio}</p>
          </div>
        </div>
        <dl className="grid sm:grid-cols-3 gap-4 mt-8 text-sm">
          {[["Categories", c.categories], ["Languages", c.languages], ["Platforms", c.platforms]].map(([k, v]) => (
            <div key={k} className="rounded-2xl border border-border bg-surface p-4"><dt className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{k}</dt><dd>{v.length ? v.join(", ") : "—"}</dd></div>
          ))}
        </dl>
        <section className="mt-8" data-testid="creator-public-stats">
          <h2 className="font-heading font-bold text-lg mb-3">Audience</h2>
          {c.verified_stats.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {c.verified_stats.map((s) => (
                <div key={s.platform} className="rounded-2xl border border-border bg-surface p-4">
                  <p className="font-semibold flex items-center gap-1">{s.platform} {s.owner_verified ? <ShieldCheck className="h-4 w-4 text-neon-lime" /> : <BadgeCheck className="h-4 w-4 text-neon-cyan" />}</p>
                  <p className="font-heading font-black text-2xl">{compact(s.followers)}</p>
                  <p className="text-xs text-muted-foreground">{s.owner_verified ? "Owner-verified · " : ""}{s.source}{s.last_synced ? ` · updated ${s.last_synced.slice(0, 10)}` : ""}</p>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">{c.followers ? `${compact(c.followers)} followers (self-reported, not verified).` : "No audience stats shared yet."}</p>}
        </section>
        {d.showcase.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading font-bold text-lg mb-3">Content</h2>
            <ul className="space-y-1 text-sm">{d.showcase.map((s, i) => s.source_url && <li key={i}><a href={s.source_url} target="_blank" rel="noopener noreferrer nofollow" className="text-brand-pink hover:underline">{s.title || `${s.platform} post`}</a></li>)}</ul>
          </section>
        )}
        {d.landing_links.length > 0 && (
          <section className="mt-8" data-testid="creator-landing-links">
            <h2 className="font-heading font-bold text-lg mb-3">Find similar creators</h2>
            <ul className="flex flex-wrap gap-2">{d.landing_links.map((l) => <li key={l.path}><Link to={l.path} className="inline-block rounded-full border border-border px-3 py-1.5 text-sm hover:border-brand-pink">{l.label}</Link></li>)}</ul>
          </section>
        )}
        <div className="mt-10 rounded-3xl bg-brand-gradient text-white p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="font-heading font-bold text-xl">Want to collaborate with {c.name}?</p>
          <Link to="/?join=brand" data-testid="creator-public-cta" className="rounded-full bg-white text-foreground px-5 py-2.5 font-semibold text-sm">Sign up as a brand to send an offer</Link>
        </div>
      </main>
      <SeoFooter />
    </div>
  );
}
